import { ChangeEvent, useCallback, useMemo, useState } from 'react'
import { Form, OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import classNames from 'classnames'

import { Descriptor, ObjectDescriptor, UiSchemaDescriptor } from '../../../models/Scorecard'
import {
  FormFieldFormTemplateMappingSource,
  FormTemplateMappingFormValues,
  FormTemplateMappingSource,
  FormTemplateMappingTarget,
  VariableFormTemplateMappingSourceId,
} from '../../../models/FormTemplate'

import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { Button } from '../../../components/Button/Button'

import { FORM_FIELD_TYPE_TO_FORM_FIELD_LABEL_MAPPING } from '../AIForms/model'

import {
  countFormFieldSourcesInDescriptor,
  countMappedFormFieldSources,
  doSourcesMatch,
  FORM_TEMPLATE_VARIABLE_SOURCE_ID_TO_LABEL_MAPPING,
  getFormFieldFormTemplateMappingSourcesFromDescriptorEntry,
  getFormFieldTypeFromDescriptor,
  getOptionsForDescriptor,
  mappingExistsForSource,
  sortDescriptors,
} from './utils'

interface FormTemplateMappingFormProps {
  targets: FormTemplateMappingTarget[]
  mapping: Record<string, FormTemplateMappingSource>
  scorecardDescriptor: ObjectDescriptor
  onSubmit: (formData: FormTemplateMappingFormValues) => Promise<void>
  submitLoading: boolean
  handleDiscard?: () => void
}

export const FormTemplateMappingForm: React.FC<FormTemplateMappingFormProps> = ({
  targets,
  mapping,
  scorecardDescriptor,
  onSubmit,
  submitLoading,
  handleDiscard,
}) => {
  const form = useForm<FormTemplateMappingFormValues>({ defaultValues: { mapping } })

  return (
    <FormProvider {...form}>
      <div className='h-100 d-flex flex-column zocks-gap-2 form-template-update-container'>
        <div className='d-flex flex-column gap-1'>
          <h3>Edit document template mapping</h3>
          <span className='paragraph-medium'>Map form fields and variables to PDF fields.</span>
        </div>
        <FormTemplateMappingRows targets={targets} scorecardDescriptor={scorecardDescriptor} />
        <div className='d-flex gap-3 align-items-center justify-content-end zocks-px-1.75 py-3 rounded-3 position-fixed form-template-update-floating-toolbar z-2'>
          {handleDiscard ? (
            <Button className='px-3 py-2 button-medium' variant='secondaryGhostButton' onClick={handleDiscard}>
              Discard
            </Button>
          ) : null}
          <Button className='px-3 py-2 button-medium' variant='primary' onClick={form.handleSubmit(onSubmit)} loading={submitLoading}>
            Save
          </Button>
        </div>
      </div>
    </FormProvider>
  )
}

interface FormTemplateMappingRowsProps {
  targets: FormTemplateMappingTarget[]
  scorecardDescriptor: ObjectDescriptor
}

const FormTemplateMappingRows: React.FC<FormTemplateMappingRowsProps> = ({ targets, scorecardDescriptor }) => {
  const { watch } = useFormContext<FormTemplateMappingFormValues>()

  const mapping = watch('mapping')

  const [showUnmappedOnly, setShowUnmappedOnly] = useState<boolean>(false)

  const variableIds = Object.values(VariableFormTemplateMappingSourceId)

  const sortedDescriptorEntries = useMemo(
    () => Object.entries(scorecardDescriptor.properties).sort(([, descriptorA], [, descriptorB]) => sortDescriptors(descriptorA, descriptorB)),
    [scorecardDescriptor],
  )

  const filteredDescriptorEntries = useMemo(
    () =>
      showUnmappedOnly
        ? sortedDescriptorEntries.filter(
            ([key, descriptor]) =>
              !getFormFieldFormTemplateMappingSourcesFromDescriptorEntry(key, descriptor).every((source) => mappingExistsForSource(source, mapping)),
          )
        : sortedDescriptorEntries,
    [mapping, showUnmappedOnly, sortedDescriptorEntries],
  )

  const totalFormFieldSourceCount = useMemo(() => countFormFieldSourcesInDescriptor(scorecardDescriptor), [scorecardDescriptor])

  const mappedFormFieldSourceCount = useMemo(() => countMappedFormFieldSources(mapping), [mapping])

  const formFieldMappingProgressPercent = useMemo(
    () => Math.floor((mappedFormFieldSourceCount / totalFormFieldSourceCount) * 100),
    [mappedFormFieldSourceCount, totalFormFieldSourceCount],
  )

  const handleShowUnmappedOnlyChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShowUnmappedOnly(e.target.checked)
  }

  return (
    <div className='d-flex flex-column'>
      <div className='d-flex flex-column gap-3 position-sticky top-0 z-1 zocks-bg-color-white pt-3'>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center gap-2'>
            <Form.Check
              type='checkbox'
              id='show-unmapped-form-fields-checkbox'
              className='d-flex align-items-center justify-content-center form-template-update-checkbox'
              checked={showUnmappedOnly}
              onChange={handleShowUnmappedOnlyChange}
            />
            <label htmlFor='show-unmapped-form-fields-checkbox' className='paragraph-medium'>
              Show unmatched fields only
            </label>
          </div>
          <div className='d-flex flex-column position-relative'>
            <p className='paragraph-medium m-0'>
              <span className='bold'>
                {mappedFormFieldSourceCount} of {totalFormFieldSourceCount}
              </span>{' '}
              <span>Form fields matched</span>
            </p>
            <div
              className='position-absolute bottom-0 z-1 zocks-bg-color-primary form-template-update-progress rounded-2'
              style={{ width: `${formFieldMappingProgressPercent}%` }}
            />
            <div className='w-100 zocks-bg-color-gray-300 form-template-update-progress rounded-2' />
          </div>
        </div>
        <div className='d-flex align-items-center rounded-top-3 zocks-bg-color-gray-200'>
          <div className='w-50 d-flex align-items-center'>
            <span className='px-3 zocks-py-0.75 lead-extra-small'>Form field / Variable</span>
            <div className='form-template-update-spacer' />
          </div>
          <span className='w-50 px-3 zocks-py-0.75 lead-extra-small'>PDF field</span>
        </div>
      </div>
      <div className='d-flex flex-column border rounded-bottom-3 zocks-border-color-gray-200'>
        {filteredDescriptorEntries.map(([key, descriptor]) => {
          const options = getOptionsForDescriptor(descriptor)

          const source: FormFieldFormTemplateMappingSource = { type: 'form_field', path: `$.${key}` }

          const uiSchemaDescriptor = scorecardDescriptor.customWidgets?.[key]

          if (!options) {
            return (
              <div className='d-flex align-items-center border-top zocks-border-color-gray-200 form-template-mapping-row'>
                <FormFieldFormTemplateMappingSourceColumn descriptor={descriptor} uiSchemaDescriptor={uiSchemaDescriptor} />
                <FormTemplateMappingTargetColumn targets={targets} source={source} />
              </div>
            )
          }

          const filteredOptions = showUnmappedOnly ? options.filter((option) => !mappingExistsForSource({ ...source, option }, mapping)) : options

          return (
            <>
              <div className='d-flex align-items-center border-top zocks-border-color-gray-200 form-template-mapping-row'>
                <FormFieldFormTemplateMappingSourceColumn descriptor={descriptor} uiSchemaDescriptor={uiSchemaDescriptor} showSpacer={true} />
                <div className='w-50 d-flex flex-column px-3 zocks-py-0.75'>
                  <span className='paragraph-medium zocks-text-gray-700 py-2'>Match each option to a PDF field:</span>
                </div>
              </div>
              {filteredOptions.map((option) => (
                <div className='d-flex align-items-center zocks-border-color-gray-200 form-template-mapping-row'>
                  <FormFieldFormTemplateMappingSourceColumn descriptor={descriptor} uiSchemaDescriptor={uiSchemaDescriptor} option={option} />
                  <FormTemplateMappingTargetColumn targets={targets} source={{ ...source, option }} />
                </div>
              ))}
            </>
          )
        })}
        {variableIds.map((variableId) => (
          <div className='d-flex align-items-center border-top zocks-border-color-gray-200 form-template-mapping-row'>
            <VariableFormTemplateMappingSourceColumn variableId={variableId} />
            <FormTemplateMappingTargetColumn targets={targets} source={{ type: 'variable', id: variableId }} />
          </div>
        ))}
      </div>
    </div>
  )
}

interface FormFieldFormTemplateMappingSourceColumnProps {
  descriptor: Descriptor
  uiSchemaDescriptor?: UiSchemaDescriptor
  option?: string
  showSpacer?: boolean
}

const FormFieldFormTemplateMappingSourceColumn: React.FC<FormFieldFormTemplateMappingSourceColumnProps> = ({
  descriptor,
  uiSchemaDescriptor,
  option,
  showSpacer = false,
}) => {
  const formFieldType = getFormFieldTypeFromDescriptor(descriptor, uiSchemaDescriptor)

  return (
    <div className='w-50 d-flex align-items-center'>
      <div className='d-flex align-items-center flex-grow-1 px-3 zocks-py-0.75 gap-2'>
        {option ? (
          <>
            <i className='bi bi-arrow-return-right' />
            <span className='paragraph-medium'>{option}</span>
          </>
        ) : (
          <>
            <div className='d-flex align-items-center gap-1'>
              <span className='paragraph-medium'>{descriptor.title}</span>
              <span className='paragraph-small zocks-text-gray-600'>({FORM_FIELD_TYPE_TO_FORM_FIELD_LABEL_MAPPING[formFieldType]})</span>
            </div>
            {descriptor.description ? (
              <OverlayTrigger
                trigger={['hover', 'focus']}
                placement='top'
                overlay={<BootstrapTooltip className='zocks-text-gray-500'>{descriptor.description}</BootstrapTooltip>}
              >
                <i className='bi bi-info-square zocks-text-gray-600' />
              </OverlayTrigger>
            ) : null}
          </>
        )}
      </div>
      {showSpacer ? (
        <div className='form-template-update-spacer' />
      ) : (
        <div className='px-3 zocks-py-0.75'>
          <i className='bi bi-arrow-right' />
        </div>
      )}
    </div>
  )
}

interface VariableFormTemplateMappingSourceColumnProps {
  variableId: VariableFormTemplateMappingSourceId
}

const VariableFormTemplateMappingSourceColumn: React.FC<VariableFormTemplateMappingSourceColumnProps> = ({ variableId }) => {
  return (
    <div className='w-50 d-flex align-items-center'>
      <div className='d-flex align-items-center flex-grow-1 px-3 zocks-py-0.75 gap-1'>
        <span className='paragraph-medium'>{FORM_TEMPLATE_VARIABLE_SOURCE_ID_TO_LABEL_MAPPING[variableId]}</span>
        <span className='paragraph-small zocks-text-gray-600'>(Variable)</span>
      </div>
      <div className='px-3 zocks-py-0.75'>
        <i className='bi bi-arrow-right' />
      </div>
    </div>
  )
}

interface FormTemplateMappingTargetColumnProps {
  targets: FormTemplateMappingTarget[]
  source: FormTemplateMappingSource
}

const FormTemplateMappingTargetColumn: React.FC<FormTemplateMappingTargetColumnProps> = ({ targets, source }) => {
  const { setValue, watch } = useFormContext<FormTemplateMappingFormValues>()

  const mapping = watch('mapping')

  const targetIds = useMemo(
    () =>
      Object.entries(mapping)
        .filter(([, mappingSource]) => doSourcesMatch(source, mappingSource))
        .map(([targetId]) => targetId),
    [mapping, source],
  )

  const targetOptions = useMemo(
    () =>
      targets
        .filter((target) => targetIds.includes(target.id) || !Object.keys(mapping).includes(target.id))
        .map((target) => ({ id: target.id, label: target.name })),
    [mapping, targetIds, targets],
  )

  const handleSelectedChanged = useCallback(
    (selectedTargetIds: string[]) => {
      const mappingEntriesWithoutSelectedTargetIds = Object.entries(mapping).filter(([targetId]) => !targetIds.includes(targetId))
      const newMappingEntries = selectedTargetIds.map((targetId) => [targetId, source])
      setValue('mapping', Object.fromEntries([...mappingEntriesWithoutSelectedTargetIds, ...newMappingEntries]))
    },
    [mapping, setValue, source, targetIds],
  )

  const selectDropdownName =
    source.type === 'form_field' ? (source.option ? `form_field_${source.path}_${source.option}` : `form_field_${source.path}`) : `variable_${source.id}`

  return (
    <div className='w-50 d-flex flex-column px-3 zocks-py-0.75' style={{ maxWidth: '50%' }}>
      <MultiSelectDropdown
        name={selectDropdownName}
        initialSelectedOptions={targetIds}
        options={targetOptions}
        onSelectedChanged={handleSelectedChanged}
        TitleComponent={
          <span
            className={classNames('paragraph-medium overflow-hidden text-nowrap', {
              'zocks-text-gray-500': !targetIds.length,
            })}
          >
            {targetIds.length
              ? targetOptions
                  .filter((option) => targetIds.includes(option.id))
                  .map((option) => option.label)
                  .join(', ')
              : 'No match found - select field or skip'}
          </span>
        }
      />
    </div>
  )
}
